import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from '../../hooks/useNotifications';
import { Notification } from '../../types/models';

function relativeTime(iso: string): string {
  const diffMs  = Date.now() - new Date(iso).getTime();
  const mins    = Math.floor(diffMs / 60_000);
  const hours   = Math.floor(diffMs / 3_600_000);
  const days    = Math.floor(diffMs / 86_400_000);
  if (mins < 1)    return 'just now';
  if (mins < 60)   return `${mins}m ago`;
  if (hours < 24)  return `${hours}h ago`;
  if (days < 7)    return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

interface NotificationRowProps {
  notification: Notification;
  onMarkRead: (id: string) => void;
}

const NotificationRow: React.FC<NotificationRowProps> = ({ notification, onMarkRead }) => (
  <button
    type="button"
    onClick={() => !notification.read && onMarkRead(notification.notificationId)}
    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0 ${
      notification.read ? '' : 'bg-green-50'
    }`}
  >
    <div className="flex items-start gap-2">
      {!notification.read && (
        <span className="mt-1.5 shrink-0 w-2 h-2 rounded-full bg-green-600" />
      )}
      <div className={notification.read ? 'pl-4' : ''}>
        <p className="text-sm font-medium text-gray-900 leading-snug">{notification.title}</p>
        <p className="text-sm text-gray-500 mt-0.5 leading-snug line-clamp-2">{notification.body}</p>
        <p className="text-xs text-gray-400 mt-1">{relativeTime(notification.createdAt)}</p>
      </div>
    </div>
  </button>
);

export const NotificationBell: React.FC = () => {
  const { notifications, unreadCount, loading, refresh, markRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const panelRef        = useRef<HTMLDivElement>(null);
  const buttonRef       = useRef<HTMLButtonElement>(null);

  // Re-fetch each time the panel opens so the list is always current
  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        panelRef.current  && !panelRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleMarkAllRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await markRead();
  };

  const handleMarkOneRead = async (id: string) => {
    await markRead([id]);
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label="Notifications"
        className="relative p-1 text-gray-500 hover:text-gray-700 transition-colors"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="text-xs text-green-700 hover:text-green-800 font-medium transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">Loading…</p>
            ) : notifications.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No notifications yet</p>
            ) : (
              notifications.map(n => (
                <NotificationRow
                  key={n.notificationId}
                  notification={n}
                  onMarkRead={handleMarkOneRead}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
